import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Vote, Plus, CheckCircle2, XCircle, Award, FileDown, Clock, Users, QrCode } from "lucide-react";
import { toast } from "sonner";

/**
 * R157B — Annual Board Election Wizard.
 * Lifecycle: draft → nominations_open → nominations_closed → voting_open →
 * voting_closed → certified.
 */
const STATUS_TONE = {
  draft: { label: "Draft", cls: "border-white/20 text-white/60" },
  nominations_open: { label: "Nominations open", cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  nominations_closed: { label: "Nominations closed", cls: "border-white/20 text-white/60" },
  voting_open: { label: "Voting open", cls: "border-orange-500/40 text-orange-300 bg-orange-500/10" },
  voting_closed: { label: "Voting closed", cls: "border-white/20 text-white/60" },
  certified: { label: "Certified", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  cancelled: { label: "Cancelled", cls: "border-rose-500/40 text-rose-300 bg-rose-500/10" },
};

const inFuture = (days) => new Date(Date.now() + days * 86400e3).toISOString().slice(0, 16);

export default function BoardElectionsPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    title: "2026 Annual Board Election",
    description: "",
    seats_available: 3,
    nominations_open_at: inFuture(0),
    nominations_close_at: inFuture(7),
    voting_open_at: inFuture(10),
    voting_close_at: inFuture(17),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/board/elections");
      setItems((data.items || []).filter((e) => !scope || e.property_id === scope));
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (eid) => {
    setSelectedId(eid);
    try {
      const { data } = await api.get(`/admin/board/elections/${eid}`);
      setDetail(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load election"); }
  };

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post("/admin/board/elections", {
        property_id: scope, ...form,
        seats_available: Number(form.seats_available),
        // convert local-datetime-input to ISO with Z
        nominations_open_at: new Date(form.nominations_open_at).toISOString(),
        nominations_close_at: new Date(form.nominations_close_at).toISOString(),
        voting_open_at: new Date(form.voting_open_at).toISOString(),
        voting_close_at: new Date(form.voting_close_at).toISOString(),
      });
      toast.success("Election created — nominations open per your schedule");
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Create failed"); }
    finally { setBusy(false); }
  };

  const decide = async (nid, accepted) => {
    try {
      await api.post(`/admin/board/elections/${selectedId}/nominations/${nid}/decision`,
        { accepted });
      toast.success(accepted ? "Nomination accepted" : "Nomination rejected");
      openDetail(selectedId);
    } catch (e) { toast.error(apiErrorMessage(e) || "Decision failed"); }
  };

  const certify = async () => {
    if (!window.confirm("Certify this election?\nThis will tally ballots, generate a certified PDF, and email every homeowner the result. This action cannot be undone.")) return;
    try {
      const { data } = await api.post(`/admin/board/elections/${selectedId}/certify`);
      toast.success(`Certified — ${data.ballots_total} ballots · emailed ${data.email_summary?.sent || 0} homeowners`);
      openDetail(selectedId);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Certify failed"); }
  };

  const cancel = async (eid) => {
    if (!window.confirm("Cancel this election? Cannot be undone.")) return;
    try {
      await api.post(`/admin/board/elections/${eid}/cancel`);
      toast.success("Cancelled");
      await load();
      if (eid === selectedId) setSelectedId(null);
    } catch (e) { toast.error(apiErrorMessage(e) || "Cancel failed"); }
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  return (
    <div className="space-y-4" data-testid="board-elections-page">
      <PageHeader
        eyebrow="Governance"
        title="Board Elections"
        subtitle="Full lifecycle: schedule nominations, review candidates, run a cryptographic secret ballot, then certify + email every homeowner."
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-64 h-9 bg-white/[0.04] border-white/10"
                          data-testid="election-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setDialogOpen(true)}
                className="ml-auto bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                data-testid="election-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New election
        </Button>
      </div>

      <Card className="p-0 border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <Vote className="h-4 w-4 text-orange-400" />
          <div className="overline">Elections — {scopeName}</div>
          <Badge variant="outline" className="text-[10px] border-white/10">{items.length}</Badge>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No elections yet. Click <strong>New election</strong> to schedule one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs">
              <tr>
                <th className="text-left px-3 py-2 text-white/60">Title</th>
                <th className="text-left px-3 py-2 text-white/60">Seats</th>
                <th className="text-left px-3 py-2 text-white/60">Nominations</th>
                <th className="text-left px-3 py-2 text-white/60">Voting</th>
                <th className="text-left px-3 py-2 text-white/60">Status</th>
                <th className="text-right px-3 py-2 text-white/60"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-white/5"
                    data-testid={`election-row-${e.id}`}>
                  <td className="px-3 py-2 font-medium">{e.title}</td>
                  <td className="px-3 py-2 text-xs">{e.seats_available}</td>
                  <td className="px-3 py-2 text-xs text-white/60">
                    {(e.nominations_open_at || "").slice(0, 10)} → {(e.nominations_close_at || "").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-xs text-white/60">
                    {(e.voting_open_at || "").slice(0, 10)} → {(e.voting_close_at || "").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline"
                            className={`text-[10px] ${STATUS_TONE[e.status]?.cls || "border-white/20"}`}>
                      {STATUS_TONE[e.status]?.label || e.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openDetail(e.id)}
                            className="text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 mr-1"
                            data-testid={`election-open-${e.id}`}>
                      Open
                    </button>
                    {e.status !== "certified" && e.status !== "cancelled" && (
                      <button onClick={() => cancel(e.id)}
                              className="text-xs px-2 py-1 rounded-md border border-white/10 text-white/50 hover:text-rose-300 hover:border-rose-500/40">
                        <XCircle className="h-3 w-3 inline mr-1" />Cancel
                      </button>
                    )}
                    {e.status === "certified" && (
                      <a href={`${API}/admin/board/elections/${e.id}/certificate.pdf`}
                         target="_blank" rel="noreferrer"
                         className="text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1"
                         data-testid={`election-cert-pdf-${e.id}`}>
                        <FileDown className="h-3 w-3" /> Cert PDF
                      </a>
                    )}
                    <a href={`${API}/board/elections/${e.id}/qr.png`}
                       target="_blank" rel="noreferrer"
                       className="text-xs px-2 py-1 rounded-md border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 inline-flex items-center gap-1 ml-1"
                       data-testid={`election-qr-${e.id}`}
                       title="Ballot QR — print on the annual meeting agenda">
                      <QrCode className="h-3 w-3" /> QR
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[600px]"
                       data-testid="election-new-dialog">
          <DialogHeader>
            <DialogTitle>Schedule a board election</DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              Set the four dates. Windows must be strictly ordered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="bg-white/[0.04] border-white/10 mt-1"
                      data-testid="election-title-input" />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea value={form.description} rows={2}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Seats available</Label>
              <Input type="number" min={1} max={15}
                      value={form.seats_available}
                      onChange={(e) => setForm({ ...form, seats_available: e.target.value })}
                      className="bg-white/[0.04] border-white/10 mt-1 w-24"
                      data-testid="election-seats-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nominations open</Label>
                <Input type="datetime-local" value={form.nominations_open_at}
                        onChange={(e) => setForm({ ...form, nominations_open_at: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-xs">Nominations close</Label>
                <Input type="datetime-local" value={form.nominations_close_at}
                        onChange={(e) => setForm({ ...form, nominations_close_at: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-xs">Voting open</Label>
                <Input type="datetime-local" value={form.voting_open_at}
                        onChange={(e) => setForm({ ...form, voting_open_at: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-xs">Voting close</Label>
                <Input type="datetime-local" value={form.voting_close_at}
                        onChange={(e) => setForm({ ...form, voting_close_at: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 [color-scheme:dark]" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
            <Button onClick={create} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="election-create-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Dialog open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[800px] max-h-[90vh] overflow-y-auto"
                       data-testid="election-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Vote className="h-4 w-4 text-orange-400" />
                  {detail.title}
                </DialogTitle>
                <DialogDescription className="text-xs text-white/60">
                  <Badge variant="outline"
                          className={`text-[10px] ${STATUS_TONE[detail.status]?.cls || "border-white/20"}`}>
                    {STATUS_TONE[detail.status]?.label || detail.status}
                  </Badge>
                  {" · "}Turnout: <b>{detail.turnout || 0}</b> voters
                  {detail.ballots_total ? <> · Ballots: <b>{detail.ballots_total}</b></> : null}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-xs text-white/70">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Nominations</div>
                  <div>{(detail.nominations_open_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div>→ {(detail.nominations_close_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Voting</div>
                  <div>{(detail.voting_open_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div>→ {(detail.voting_close_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Nominations</div>
                {detail.nominations.length === 0 ? (
                  <div className="text-xs text-white/40">No nominations yet.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.nominations.map((n) => (
                      <div key={n.id}
                            className="flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.02] p-2"
                            data-testid={`nomination-${n.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{n.nominee_name}</div>
                          <div className="text-[11px] text-white/50">
                            Nominated by {n.nominated_by_name || "—"}
                            {n.self_nom ? " (self)" : ""}
                            {detail.tally && detail.tally[n.id] !== undefined
                              ? <> · <b className="text-orange-300">{detail.tally[n.id]} vote{detail.tally[n.id] === 1 ? "" : "s"}</b></>
                              : null}
                          </div>
                          {n.nominee_bio && (
                            <div className="text-[11px] text-white/60 mt-1 line-clamp-2">{n.nominee_bio}</div>
                          )}
                        </div>
                        {n.accepted === null && (detail.status === "nominations_open" || detail.status === "nominations_closed") && (
                          <div className="flex gap-1">
                            <button onClick={() => decide(n.id, true)}
                                    className="text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                    data-testid={`accept-${n.id}`}>
                              <CheckCircle2 className="h-3 w-3 inline mr-1" />Accept
                            </button>
                            <button onClick={() => decide(n.id, false)}
                                    className="text-xs px-2 py-1 rounded-md border border-white/10 text-white/50 hover:text-rose-300 hover:border-rose-500/40"
                                    data-testid={`reject-${n.id}`}>
                              <XCircle className="h-3 w-3 inline mr-1" />Reject
                            </button>
                          </div>
                        )}
                        {n.accepted === true && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">Accepted</Badge>}
                        {n.accepted === false && <Badge variant="outline" className="border-rose-500/40 text-rose-300">Rejected</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                {detail.status === "voting_closed" && (
                  <Button onClick={certify} className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid="election-certify-btn">
                    <Award className="h-4 w-4 mr-1" /> Certify + email results
                  </Button>
                )}
                {detail.status === "certified" && (
                  <a href={`${API}/admin/board/elections/${detail.id}/certificate.pdf`}
                     target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-4 h-9">
                    <FileDown className="h-4 w-4" /> Download certified PDF
                  </a>
                )}
                <Button variant="outline" onClick={() => setSelectedId(null)}
                        className="border-white/10">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
